import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { registerDialogController } from "./dialogService.js";
import "./dialogs.css";

const TOAST_DURATION_MS = 5200;

export default function DialogProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const [dialog, setDialog] = useState(null);
  const promptInputRef = useRef(null);
  const nextToastId = useRef(1);

  const dismissToast = useCallback((id) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const notify = useCallback(
    ({
      message,
      type = "info",
      duration,
      durationMs,
      presentation,
      centered,
      title,
      okLabel,
      confirmLabel,
    } = {}) => {
      const id = nextToastId.current;
      nextToastId.current += 1;
      const wantsToast = presentation === "toast";
      const resolvedDuration =
        Number.isFinite(duration) ? duration :
        Number.isFinite(durationMs) ? durationMs :
        wantsToast ? TOAST_DURATION_MS : 0;
      const text = String(message ?? "");

      if (!wantsToast) {
        setDialog({
          id,
          kind: "notice",
          message: text,
          title: title || (type === "error" ? "Message important" : "Information"),
          confirmLabel: confirmLabel || okLabel || "Fermer",
          cancelLabel: "",
          defaultValue: "",
          type,
          resolve: null,
        });

        if (resolvedDuration > 0) {
          window.setTimeout(() => {
            setDialog((current) => (current?.id === id ? null : current));
          }, resolvedDuration);
        }

        return id;
      }

      setToasts((current) => [...current, { id, message: text, type }]);

      if (resolvedDuration > 0) {
        window.setTimeout(() => dismissToast(id), resolvedDuration);
      }

      return id;
    },
    [dismissToast]
  );

  const openDecisionDialog = useCallback((kind, options = {}) => {
    return new Promise((resolve) => {
      setDialog({
        kind,
        message: String(options.message ?? ""),
        title: options.title || (kind === "prompt" ? "Saisie requise" : "Confirmation"),
        confirmLabel: options.confirmLabel || options.okLabel || "Confirmer",
        cancelLabel: options.cancelLabel || "Annuler",
        defaultValue: options.defaultValue || "",
        resolve,
      });
    });
  }, []);

  const confirm = useCallback(
    (options) => openDecisionDialog("confirm", options),
    [openDecisionDialog]
  );

  const prompt = useCallback(
    (options) => openDecisionDialog("prompt", options),
    [openDecisionDialog]
  );

  const controller = useMemo(
    () => ({ notify, confirm, prompt }),
    [notify, confirm, prompt]
  );

  useEffect(() => registerDialogController(controller), [controller]);

  useEffect(() => {
    if (dialog?.kind === "prompt") {
      promptInputRef.current?.focus();
      promptInputRef.current?.select();
    }
  }, [dialog]);

  const closeDialog = useCallback(
    (value) => {
      if (!dialog) return;
      if (typeof dialog.resolve === "function") {
        dialog.resolve(value);
      }
      setDialog(null);
    },
    [dialog]
  );

  const confirmCurrentDialog = useCallback(() => {
    if (!dialog) return;
    if (dialog.kind === "notice") {
      closeDialog(true);
      return;
    }
    if (dialog.kind === "prompt") {
      closeDialog(promptInputRef.current?.value ?? "");
      return;
    }
    closeDialog(true);
  }, [closeDialog, dialog]);

  const dialogLayer =
    typeof document !== "undefined"
      ? createPortal(
          <>
            <div className="planning-toast-region" aria-live="polite" aria-atomic="true">
              {toasts.map((toast) => (
                <div
                  key={toast.id}
                  className={`planning-toast planning-toast--${toast.type}`}
                  data-testid="planning-toast"
                >
                  <div className="planning-toast__message">{toast.message}</div>
                  <button
                    type="button"
                    className="planning-toast__close"
                    aria-label="Fermer la notification"
                    onClick={() => dismissToast(toast.id)}
                  >
                    x
                  </button>
                </div>
              ))}
            </div>

            {dialog ? (
              <div
                className="planning-dialog-backdrop"
                role="presentation"
                onMouseDown={(event) => {
                  if (event.target === event.currentTarget && dialog.kind !== "notice") {
                    closeDialog(false);
                  }
                }}
              >
                <div
                  className={`planning-dialog planning-dialog--${dialog.kind}${dialog.type ? ` planning-dialog--${dialog.type}` : ""}`}
                  role={dialog.kind === "notice" && dialog.type === "error" ? "alertdialog" : "dialog"}
                  aria-modal="true"
                  aria-labelledby="planning-dialog-title"
                  data-testid="planning-dialog"
                >
                  {dialog.kind === "notice" ? (
                    <button
                      type="button"
                      className="planning-dialog__close"
                      aria-label="Fermer le message"
                      onClick={() => closeDialog(true)}
                    >
                      x
                    </button>
                  ) : null}
                  <h2 id="planning-dialog-title" className="planning-dialog__title">
                    {dialog.title}
                  </h2>
                  <p className="planning-dialog__message">{dialog.message}</p>
                  {dialog.kind === "prompt" ? (
                    <input
                      ref={promptInputRef}
                      className="planning-dialog__input"
                      defaultValue={dialog.defaultValue}
                      data-testid="planning-dialog-input"
                      onKeyDown={(event) => {
                        if (event.key === "Enter") confirmCurrentDialog();
                        if (event.key === "Escape") closeDialog(null);
                      }}
                    />
                  ) : null}
                  <div className="planning-dialog__actions">
                    {dialog.kind !== "notice" ? (
                      <button
                        type="button"
                        className="planning-dialog__button planning-dialog__button--secondary"
                        data-testid="planning-dialog-cancel"
                        onClick={() => closeDialog(dialog.kind === "prompt" ? null : false)}
                      >
                        {dialog.cancelLabel}
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="planning-dialog__button planning-dialog__button--primary"
                      data-testid="planning-dialog-confirm"
                      onClick={confirmCurrentDialog}
                    >
                      {dialog.confirmLabel}
                    </button>
                  </div>
                </div>
              </div>
            ) : null}
          </>,
          document.body
        )
      : null;

  return (
    <>
      {children}
      {dialogLayer}
    </>
  );
}
